import { riverChainData, riverConfigFor, riverLendersByChain } from '@1delta/data-sdk'
import { RiverTroveManagerAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { riverLenderKey } from '../../../lending/public-data/river/convertPublic'

/**
 * River (Satoshi rebrand — Liquity V1/Prisma-lineage omni-chain CDP) oracle
 * price fetcher.
 *
 * Each market's `TroveManager.fetchPrice()` returns the COLLATERAL price in USD
 * (1e18-scaled) — the same read the public-data fetcher makes. `fetchPrice` is
 * nonpayable but simulates cleanly under an `eth_call` multicall. Direct USD
 * (PASS 1) → feeds the shared `usdPrices` map, so River collateral on chains
 * where no other integrated protocol prices it (BNB / Base / Hemi bespoke LSTs)
 * is covered from River's own oracle. The debt token (satUSD) is emitted pegged
 * to $1 (matching `convertPublic`).
 */

interface RiverMarketMeta {
  lender: string
  chainId: string
  index: number
  collToken: string
  debtToken?: string
}

/** Build one `fetchPrice()` call per market across every River deployment. */
export function getRiverCalls(
  chainId: string,
): FetcherResult<RiverMarketMeta>[] {
  const results: FetcherResult<RiverMarketMeta>[] = []

  for (const lender of riverLendersByChain(chainId)) {
    const markets = riverChainData(lender, chainId)?.markets ?? []
    if (markets.length === 0) continue
    const debtToken = riverConfigFor(lender, chainId)?.debtToken

    for (const m of markets) {
      if (!m.troveManager || !m.collToken) continue
      const calls: MulticallCall[] = [
        { address: m.troveManager, name: 'fetchPrice', params: [] },
      ]
      results.push({
        calls,
        meta: {
          lender,
          chainId,
          index: m.index,
          collToken: m.collToken.toLowerCase(),
          debtToken: debtToken?.toLowerCase(),
        },
        lender: riverLenderKey(lender, chainId, m.index),
      })
    }
  }

  return results
}

/**
 * Parse a market's `fetchPrice` into direct-USD entries: the collateral at its
 * on-chain USD price, and satUSD pegged to $1. Keyed under the per-market
 * `RIVER_<chain>_<index>` lender.
 */
export function parseRiverResults(
  data: any[],
  meta: RiverMarketMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const key = riverLenderKey(meta.lender, chainId, meta.index)
  const entries: OraclePriceEntry[] = []

  const raw = data?.[0]
  const price =
    typeof raw === 'bigint' ? raw : raw != null && raw !== '0x' ? BigInt(raw) : 0n
  // Sanity-bound (see the Liquity fetcher): a mis-scaled non-18-dec feed read
  // is dropped so it can't pollute the shared price map; the branch's
  // collateral then falls back to another protocol's price.
  const collUSD = Number(price) / 1e18
  if (collUSD > 0 && collUSD < 1e9) {
    entries.push({
      asset: meta.collToken,
      price: collUSD,
      priceUSD: collUSD,
      marketUid: createMarketUid(chainId, key, meta.collToken),
      targetLender: key,
      description: 'River TroveManager oracle (USD)',
    })
  }

  if (meta.debtToken) {
    entries.push({
      asset: meta.debtToken,
      price: 1,
      priceUSD: 1,
      marketUid: createMarketUid(chainId, key, meta.debtToken),
      targetLender: key,
      description: 'River satUSD (par)',
      staticBase: true,
      baseAsset: meta.debtToken,
    })
  }

  return entries
}

/** ABI for the River oracle call (`fetchPrice`). */
export function getRiverAbi() {
  return RiverTroveManagerAbi
}

/** River Oracle Fetcher module. */
export const riverFetcher = {
  getCalls: getRiverCalls,
  parse: parseRiverResults,
  getAbi: getRiverAbi,
}
